import { useReducer, useEffect, useCallback, useState, useMemo } from 'react';

import debounce from 'lodash/debounce';
import isFunction from 'lodash/isFunction';
import last from 'lodash/last';

import { GridWidgetsContext } from './GridWidgetsContext';
import { useDrag } from './hooks';
import reducer, {
  SET_STATE_WIDGETS,
  SET_SPACING,
  SET_BOARD_WIDTH,
  SET_WIDGETS_SIZES,
  SET_DRAG_INFO,
} from './reducer';

export const GridWidgetsProvider = ({ children, spacing, state, onChange }) => {
  const [
    {
      dragInfo,
      stateWidgets,
      widgetPositions,
      rowPositions,
      temporaryWidgetPositions,
      temporaryRowPositions,
    },
    dispatch,
  ] = useReducer(reducer, {});

  useEffect(() => {
    dispatch({
      type: SET_STATE_WIDGETS,
      payload: { stateWidgets: state },
    });
  }, [state]);

  useEffect(() => {
    dispatch({
      type: SET_SPACING,
      payload: { spacing },
    });
  }, [spacing]);

  const handleResizeBoardWidth = useCallback(({ width }) => {
    dispatch({
      type: SET_BOARD_WIDTH,
      payload: { boardWidth: width },
    });
  }, []);

  const [widgetSizes, setWidgetSizes] = useState({});

  const debouncedSetSizes = useCallback(
    debounce((widgetSizes) => {
      dispatch({
        type: SET_WIDGETS_SIZES,
        payload: { widgetSizes },
      });
    }, 0),
    []
  );

  useEffect(() => {
    debouncedSetSizes(widgetSizes);
    return () => debouncedSetSizes.cancel();
  }, [widgetSizes]);

  const handleResizeWidgetHeight = useCallback(({ id, height }) => {
    setWidgetSizes((state) => ({
      ...state,
      [id]: { height },
    }));
  }, []);

  const boardHeight = useMemo(
    () => last(temporaryRowPositions || rowPositions)?.to || 0,
    [temporaryRowPositions, rowPositions]
  );

  const handleSetDragInfo = useCallback((dragInfo) => {
    dispatch({
      type: SET_DRAG_INFO,
      payload: { dragInfo },
    });
  }, []);

  const { handleMouseDownDrag } = useDrag({
    onSetDragInfo: handleSetDragInfo,
    boardHeight,
  });

  useEffect(() => {
    if (isFunction(onChange) && stateWidgets) {
      onChange(stateWidgets);
    }
  }, [onChange, JSON.stringify(stateWidgets)]);

  return (
    <GridWidgetsContext.Provider
      value={{
        dragInfo,
        stateWidgets,
        widgetPositions,
        rowPositions,
        temporaryWidgetPositions,
        temporaryRowPositions,
        boardHeight,
        widgetSizes,
        onMouseDownDrag: handleMouseDownDrag,
        onResizeBoardWidth: handleResizeBoardWidth,
        onResizeWidgetHeight: handleResizeWidgetHeight,
      }}
    >
      {children}
    </GridWidgetsContext.Provider>
  );
};

export default GridWidgetsProvider;
